import { useCallback, useMemo } from 'react'

import { getRouteApi } from '@tanstack/react-router'

import { useUpdateURL } from '@/hooks/use-update-url'

import { withValue } from '../filter-values'

import type { SearchFilter } from '../types'

const Route = getRouteApi('/_dashboard/search/')

function asArray(v: SearchFilter['v']): Array<string> {
	return Array.isArray(v) ? v : [v]
}

/**
 * URL-backed mutator for the structured `ft` array.
 *
 * `include` / `exclude` merge per field via `withValue` (`eq` → `in`,
 * `neq` → `not_in`, no duplicates). Every +/− action on the page goes
 * through them.
 * `removeValue` downgrades `in` → `eq` (and `not_in` → `neq`) when one
 * value remains.
 */
export function useAppliedFilters() {
	const filtersFromUrl = Route.useSearch({ select: (s) => s.ft })
	const { updateURL } = useUpdateURL()

	const filters = useMemo<Array<SearchFilter>>(
		() => filtersFromUrl ?? [],
		[filtersFromUrl],
	)

	const setFilters = useCallback(
		(next: Array<SearchFilter>) => {
			updateURL({ search: { ft: next.length > 0 ? next : undefined } })
		},
		[updateURL],
	)

	const include = useCallback(
		(field: string, value: string) => {
			const next = withValue(filters, 'include', field, value)
			if (next !== filters) setFilters(next)
		},
		[filters, setFilters],
	)

	const exclude = useCallback(
		(field: string, value: string) => {
			const next = withValue(filters, 'exclude', field, value)
			if (next !== filters) setFilters(next)
		},
		[filters, setFilters],
	)

	const removeFilter = useCallback(
		(index: number) => {
			const copy = filters.filter((_, i) => i !== index)
			setFilters(copy)
		},
		[filters, setFilters],
	)

	const removeValue = useCallback(
		(index: number, value: string) => {
			const target = filters[index]
			if (!target) return
			const values = asArray(target.v).filter((v) => v !== value)
			if (values.length === 0) {
				removeFilter(index)
				return
			}
			const next = [...filters]
			const isPositive = target.o === 'in' || target.o === 'eq'
			next[index] = {
				...target,
				o: isPositive
					? values.length === 1
						? 'eq'
						: 'in'
					: values.length === 1
						? 'neq'
						: 'not_in',
				v: values.length === 1 ? values[0] : values,
			}
			setFilters(next)
		},
		[filters, removeFilter, setFilters],
	)

	const clearAll = useCallback(() => {
		setFilters([])
	}, [setFilters])

	return {
		filters,
		include,
		exclude,
		removeFilter,
		removeValue,
		clearAll,
	}
}
